/**
 * Outcome E: SimConnect drop after a completed arrival / park is overnight
 * at that field — not a teleport home and not a new ferry because the link died.
 *
 * Mid-flight / crash-in-air drop stays last-known airborne. It must not latch
 * overnight. We do not distinguish a mid-air crash from a mid-air disconnect:
 * last live sample airborne → no parked latch.
 */
import { icaoKey } from "./curfew";
import {
  alreadyNotifiedOvernight,
  applyOvernightToMissions,
  collapseExistingSandwich,
  isOpenEmptyFerry,
  nextRevenueFromField,
  overnightNote,
  pendingCallFromDecision,
  sandwichFerryIds,
  type OvernightDecision,
  type PendingOvernightCall,
} from "./ferryOvernight";
import { phaseIsAirborne, phaseIsArrived, type FlightPhaseId } from "./flightPhase";
import { nearestDestination, type FusedPosition } from "./position";
import { sampleIsAirborne, type SimPhaseSample } from "./simDraft";
import type { AircraftSpec, CharterRequest, Mission, SceneryAirport } from "./types";

/** Parked / at-gate when last live sample is this close to a Destinations field. */
export const PARKED_LATCH_MAX_NM = 8;
/** Same-evening home ferry still on the board — overnight (and hotel) does not stick. */
export const IMMINENT_HOME_FERRY_H = 8;

export type ParkedLatchReason =
  | "parked"
  | "mid_flight"
  | "never_arrived"
  | "no_airport"
  | "no_history";

export type ParkedLatch = {
  latched: boolean;
  reason: ParkedLatchReason;
  icao: string | null;
  onGround: boolean;
  sawFlight: boolean;
  lastPhase: FlightPhaseId | null;
  lastAtUtc: string | null;
};

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = icaoKey(a);
  const right = icaoKey(b);
  return Boolean(left) && left === right;
}

function asMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function speedKt(sample: Pick<SimPhaseSample, "groundSpeed" | "airspeed">): number {
  if (typeof sample.groundSpeed === "number" && Number.isFinite(sample.groundSpeed)) return Math.abs(sample.groundSpeed);
  if (typeof sample.airspeed === "number" && Number.isFinite(sample.airspeed)) return Math.abs(sample.airspeed);
  return 0;
}

export function historySawAirborne(history: ReadonlyArray<SimPhaseSample>): boolean {
  return history.some((s) => sampleIsAirborne(s) || phaseIsAirborne(s.phase));
}

export function historySawArrival(history: ReadonlyArray<SimPhaseSample>): boolean {
  return history.some((s) => phaseIsArrived(s.phase));
}

function lastIsParkedSurface(last: SimPhaseSample, sawArrival: boolean): boolean {
  if (sampleIsAirborne(last) || phaseIsAirborne(last.phase)) return false;
  if (last.phase === "takeoff") return false;
  if (last.phase === "parked" || last.phase === "landed" || last.phase === "on_ground") return last.onGround !== false;
  if (last.phase === "taxi" && sawArrival && last.onGround !== false) return true;
  return last.onGround === true && speedKt(last) < 20 && sawArrival;
}

export function resolveParkedIcao(input: {
  lastKnown?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  destIcao?: string | null;
  parkedIcao?: string | null;
  sawArrival?: boolean;
}): string | null {
  if (input.lastKnown && input.scenery?.length) {
    const near = nearestDestination(input.lastKnown, [...input.scenery], PARKED_LATCH_MAX_NM);
    if (near?.airport.icao) return icaoKey(near.airport.icao);
  }
  if (input.sawArrival) {
    const dest = icaoKey(input.destIcao);
    if (dest) return dest;
  }
  return icaoKey(input.parkedIcao) || null;
}

/**
 * Flight-complete / parked latch. Mid-flight last sample never latches.
 */
export function evaluateParkedLatch(input: {
  history: ReadonlyArray<SimPhaseSample>;
  lastKnown?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  destIcao?: string | null;
  parkedIcao?: string | null;
}): ParkedLatch {
  const history = [...input.history].sort((a, b) => (asMs(a.atUtc) ?? 0) - (asMs(b.atUtc) ?? 0));
  const last = history[history.length - 1];
  if (!last) {
    return {
      latched: false,
      reason: "no_history",
      icao: null,
      onGround: input.lastKnown?.onGround === true,
      sawFlight: false,
      lastPhase: null,
      lastAtUtc: input.lastKnown?.atUtc ?? null,
    };
  }
  const sawAir = historySawAirborne(history);
  const sawArrival = historySawArrival(history);
  const sawFlight = sawAir || sawArrival;
  if (sampleIsAirborne(last) || phaseIsAirborne(last.phase)) {
    return {
      latched: false,
      reason: "mid_flight",
      icao: null,
      onGround: false,
      sawFlight,
      lastPhase: last.phase,
      lastAtUtc: last.atUtc,
    };
  }
  if (!sawFlight || !lastIsParkedSurface(last, sawArrival || sawAir)) {
    return {
      latched: false,
      reason: "never_arrived",
      icao: null,
      onGround: last.onGround === true,
      sawFlight,
      lastPhase: last.phase,
      lastAtUtc: last.atUtc,
    };
  }
  const icao = resolveParkedIcao({
    lastKnown: input.lastKnown,
    scenery: input.scenery,
    destIcao: input.destIcao,
    parkedIcao: input.parkedIcao,
    sawArrival: sawArrival || sawAir,
  });
  if (!icao) {
    return {
      latched: false,
      reason: "no_airport",
      icao: null,
      onGround: true,
      sawFlight,
      lastPhase: last.phase,
      lastAtUtc: last.atUtc,
    };
  }
  return {
    latched: true,
    reason: "parked",
    icao,
    onGround: true,
    sawFlight,
    lastPhase: last.phase,
    lastAtUtc: last.atUtc,
  };
}

export function persistLastKnownOnDisconnect(pos?: FusedPosition | null): FusedPosition | null {
  if (!pos || !Number.isFinite(pos.lat) || !Number.isFinite(pos.lon)) return pos ?? null;
  return { ...pos, source: "last_known", stale: true };
}

function arrivalMissionAt(
  missions: ReadonlyArray<Mission>,
  icao: string,
  aircraftId?: string,
): Mission | null {
  const rows = missions.filter((m) => {
    if (m.status === "cancelled") return false;
    if (aircraftId && m.aircraftId && m.aircraftId !== aircraftId) return false;
    return sameIcao(m.destIcao, icao);
  });
  return (
    rows.find((m) => m.status === "in-progress") ??
    rows.find((m) => m.status === "completed") ??
    rows.find((m) => m.status === "pending") ??
    null
  );
}

export function resolveLiveTail(
  fleet: ReadonlyArray<AircraftSpec>,
  missions: ReadonlyArray<Mission>,
): AircraftSpec | null {
  const inflight = missions.find((m) => m.status === "in-progress");
  if (inflight) {
    return fleet.find((a) => a.id === inflight.aircraftId || a.registration === inflight.registration) ?? null;
  }
  const arrived = missions.find((m) => m.status === "completed" || m.status === "pending");
  if (arrived) {
    const hit = fleet.find((a) => a.id === arrived.aircraftId || a.registration === arrived.registration);
    if (hit) return hit;
  }
  return fleet.find((a) => a.status === "available") ?? fleet[0] ?? null;
}

export function imminentHomeFerry(input: {
  awayIcao: string;
  homeIcao: string;
  aircraftId?: string;
  missions: ReadonlyArray<Mission>;
  now?: Date;
  windowH?: number;
}): Mission | null {
  const away = icaoKey(input.awayIcao);
  const home = icaoKey(input.homeIcao);
  if (!away || !home || away === home) return null;
  const nowMs = (input.now ?? new Date()).getTime();
  const latest = nowMs + (input.windowH ?? IMMINENT_HOME_FERRY_H) * 3600_000;
  for (const m of input.missions) {
    if (!isOpenEmptyFerry(m)) continue;
    if (input.aircraftId && m.aircraftId && m.aircraftId !== input.aircraftId) continue;
    if (!sameIcao(m.originIcao, away) || !sameIcao(m.destIcao, home)) continue;
    const dep = asMs(m.departureIso);
    if (dep == null || dep > latest) continue;
    return m;
  }
  return null;
}

export function overnightSticksAtField(input: {
  awayIcao: string;
  homeIcao: string;
  aircraftId?: string;
  missions: ReadonlyArray<Mission>;
  inbox?: ReadonlyArray<CharterRequest>;
  now?: Date;
}): boolean {
  const away = icaoKey(input.awayIcao);
  const home = icaoKey(input.homeIcao);
  if (!away || away === home) return false;
  const next = nextRevenueFromField({
    icao: away,
    afterUtc: (input.now ?? new Date()).getTime(),
    aircraftId: input.aircraftId,
    missions: input.missions,
    inbox: input.inbox ?? [],
  });
  if (next) return true;
  if (input.missions.some((m) => sameIcao(m.overnightAtIcao, away))) return true;
  return !imminentHomeFerry({
    awayIcao: away,
    homeIcao: home,
    aircraftId: input.aircraftId,
    missions: input.missions,
    now: input.now,
  });
}

function stampStay(mission: Mission, icao: string, homeIcao: string): Mission {
  const note = overnightNote(icao, homeIcao);
  return {
    ...mission,
    overnightAtIcao: icaoKey(icao),
    weatherNote: mission.weatherNote?.includes("Overnight")
      ? mission.weatherNote
      : `Overnight at ${icaoKey(icao)} — last known park after SimConnect drop.`,
    dispatchWarning: mission.dispatchWarning?.includes("OVERNIGHT") ? mission.dispatchWarning : note,
    purpose:
      mission.flightType === "ferry"
        ? mission.purpose
        : /overnight/i.test(mission.purpose)
          ? mission.purpose
          : `${mission.purpose} · overnight ${icaoKey(icao)}`,
  };
}

export function applyParkedStayToMissions(
  missions: Mission[],
  icao: string,
  homeIcao: string,
  aircraftId?: string,
): Mission[] {
  const field = icaoKey(icao);
  if (!field) return missions;
  return missions.map((m) => {
    if (m.status === "cancelled") return m;
    if (aircraftId && m.aircraftId && m.aircraftId !== aircraftId) return m;
    if (sameIcao(m.destIcao, field) || sameIcao(m.originIcao, field) || sameIcao(m.overnightAtIcao, field)) {
      return stampStay(m, field, homeIcao);
    }
    return m;
  });
}

export type ParkedDisconnectApply = {
  changed: boolean;
  latched: boolean;
  reason: ParkedLatchReason;
  icao: string | null;
  lastKnownPosition: FusedPosition | null;
  fleet: AircraftSpec[];
  missions: Mission[];
  overnight: OvernightDecision | null;
  pendingOvernightCall: PendingOvernightCall | null;
  notice: string;
};

/**
 * Persist last-known on-ground field as career truth. Never invents a home
 * ferry or a teleport. Overnight + Megan only when the stay sticks (or a
 * sandwich was cancelled).
 */
export function applyParkedSimDisconnect(input: {
  history: ReadonlyArray<SimPhaseSample>;
  lastKnown?: FusedPosition | null;
  scenery?: ReadonlyArray<SceneryAirport>;
  fleet: AircraftSpec[];
  missions: Mission[];
  inbox?: ReadonlyArray<CharterRequest>;
  homeIcao: string;
  now?: Date;
}): ParkedDisconnectApply {
  const now = input.now ?? new Date();
  const home = icaoKey(input.homeIcao) || "CYPD";
  const persisted = persistLastKnownOnDisconnect(input.lastKnown ?? null);
  const tail = resolveLiveTail(input.fleet, input.missions);
  const arrivalGuess =
    input.missions.find((m) => m.status === "in-progress") ??
    input.missions.find((m) => m.status === "completed") ??
    input.missions.find((m) => m.status === "pending");
  const latch = evaluateParkedLatch({
    history: input.history,
    lastKnown: input.lastKnown,
    scenery: input.scenery,
    destIcao: arrivalGuess?.destIcao,
    parkedIcao: tail?.lastAirportIcao,
  });

  const lastKnownChanged = Boolean(
    persisted &&
      (persisted.source !== input.lastKnown?.source || persisted.stale !== input.lastKnown?.stale),
  );

  const empty: ParkedDisconnectApply = {
    changed: lastKnownChanged,
    latched: false,
    reason: latch.reason,
    icao: latch.icao,
    lastKnownPosition: persisted ?? input.lastKnown ?? null,
    fleet: input.fleet,
    missions: input.missions,
    overnight: null,
    pendingOvernightCall: null,
    notice: "",
  };

  if (!latch.latched || !latch.icao || !tail) {
    return empty;
  }

  const icao = latch.icao;
  const fleet = input.fleet.map((a) =>
    a.id === tail.id || a.registration === tail.registration ? { ...a, lastAirportIcao: icao } : a,
  );
  let missions = input.missions;
  const inbox = [...(input.inbox ?? [])];
  const collapsed = collapseExistingSandwich({
    missions,
    inbox,
    homeIcao: home,
    aircraftId: tail.id,
  });
  missions = collapsed.missions;
  let overnight = collapsed.overnight;
  const sandwichLeft = sandwichFerryIds({
    awayIcao: icao,
    homeIcao: home,
    aircraftId: tail.id,
    missions,
  });
  if (!overnight && sandwichLeft.length) {
    const again = collapseExistingSandwich({
      missions,
      inbox,
      homeIcao: home,
      aircraftId: tail.id,
    });
    missions = again.missions;
    overnight = again.overnight;
  }

  const sticks = overnightSticksAtField({
    awayIcao: icao,
    homeIcao: home,
    aircraftId: tail.id,
    missions,
    inbox: input.inbox,
    now,
  });
  if (sticks && !sameIcao(icao, home)) {
    if (overnight) {
      missions = applyOvernightToMissions(missions, overnight);
    } else {
      missions = applyParkedStayToMissions(missions, icao, home, tail.id);
      const parent = arrivalMissionAt(missions, icao, tail.id);
      if (parent && !overnight) {
        overnight = {
          icao,
          homeIcao: home,
          aircraftId: tail.id,
          missionId: parent.id,
          crewId: undefined,
          nextOriginIcao: icao,
          nextDestIcao: icaoKey(parent.destIcao) === icao ? icaoKey(parent.originIcao) || icao : icaoKey(parent.destIcao) || icao,
          cancelledFerryIds: [],
          cancelledRoutes: [],
          note: overnightNote(icao, home),
          brief: "",
          key: `${tail.id}:${icao}:${parent.id}`,
        };
      }
    }
  }

  const ferriesCancelled = Boolean(overnight?.cancelledFerryIds.length);
  const nextFromField = nextRevenueFromField({
    icao,
    afterUtc: now.getTime(),
    aircraftId: tail.id,
    missions,
    inbox: input.inbox ?? [],
  });
  const ring =
    sticks &&
    !sameIcao(icao, home) &&
    overnight &&
    (ferriesCancelled || nextFromField) &&
    !alreadyNotifiedOvernight(missions, overnight)
      ? pendingCallFromDecision(
          {
            ...overnight,
            brief:
              overnight.brief ||
              [
                "Celtic Air Dispatch.",
                `Schedule change: overnight at ${icao} after the sim parked — aircraft stays there.`,
                nextFromField
                  ? `Morning departure stays ${icaoKey(nextFromField.originIcao)}–${icaoKey(nextFromField.destIcao)}.`
                  : "",
                ferriesCancelled ? ` Empty ${overnight.cancelledRoutes.join(" and ")} cancelled.` : "",
                "No inbox message — this is the call.",
              ]
                .filter(Boolean)
                .join(" "),
          },
          now,
        )
      : null;

  const posNote = sameIcao(icao, home)
    ? `PARKED  last known ${icao} (home)`
    : sticks
      ? `PARKED OVERNIGHT  last known ${icao} — no ferry home because SimConnect dropped`
      : `PARKED  last known ${icao} — home ferry still on the board`;

  return {
    changed: true,
    latched: true,
    reason: "parked",
    icao,
    lastKnownPosition: persisted ?? input.lastKnown ?? null,
    fleet,
    missions,
    overnight: sticks ? overnight : null,
    pendingOvernightCall: ring,
    notice: posNote,
  };
}
